import React, { useState, useEffect, useMemo } from "react";
import { X, ChevronLeft, ChevronRight, ArrowLeft, Camera } from "lucide-react";
import { GALLERY_CATEGORIES, getCategoryImages, getHardcodedSubImages } from "../mock";
import { listPhotos, photoUrl, listGalleryCategories } from "../api";

export default function Gallery() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [lightboxIdx, setLightboxIdx] = useState(0);
  const [adminPhotos, setAdminPhotos] = useState([]);
  const [customCategories, setCustomCategories] = useState([]);
  const [brokenCovers, setBrokenCovers] = useState(new Set());

  useEffect(() => {
    listPhotos().then(setAdminPhotos).catch(() => {});
    listGalleryCategories().then(setCustomCategories).catch(() => {});
  }, []);

  // Merge hardcoded categories with admin-added ones (custom ones at the end)
  const allCategories = useMemo(() => {
    const custom = customCategories.map(c => ({
      id: c.id,
      name: c.name,
      cover: null, // will fall back to uploaded photo or placeholder
      custom: true
    }));
    return [...GALLERY_CATEGORIES, ...custom];
  }, [customCategories]);

  // Combine admin-uploaded photos with default hardcoded photos.
  // Merge hardcoded real photos + admin-uploaded.
  // If both are empty, fall back to the single book.fr cover.
  const getImages = (catId) => {
    const hardcoded = getHardcodedSubImages(catId);
    const uploaded = adminPhotos.filter(p => p.category === catId).map(p => photoUrl(p.url));
    const all = [...hardcoded, ...uploaded];
    if (all.length > 0) return all;
    return getCategoryImages(catId);
  };

  // Resolve a cover image for a category — prefer hardcoded, fallback to first uploaded photo
  const getCover = (cat) => {
    const hardcoded = getHardcodedSubImages(cat.id);
    if (hardcoded.length > 0) return hardcoded[0];
    const firstUploaded = adminPhotos.find(p => p.category === cat.id);
    if (firstUploaded) return photoUrl(firstUploaded.url);
    return cat.cover || null;
  };

  const images = activeCategory ? getImages(activeCategory.id) : [];

  const openLightbox = (idx) => {
    setLightboxIdx(idx);
    setLightbox(images[idx]);
  };

  const showNav = (dir) => {
    const newIdx = (lightboxIdx + dir + images.length) % images.length;
    setLightboxIdx(newIdx);
    setLightbox(images[newIdx]);
  };

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-12 fade-in">
          <p className="text-gold text-sm tracking-[0.3em] uppercase mb-4">Portfolio</p>
          <h1 className="text-5xl md:text-6xl mb-6 section-title"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {activeCategory ? activeCategory.name : "Galeries"}
          </h1>
          <div className="gold-divider mb-6" />
          {!activeCategory && (
            <p className="text-gray-400 max-w-2xl mx-auto">
              Découvrez mon univers à travers une sélection de créations.
              {" "}Cliquez sur une catégorie pour explorer.
            </p>
          )}
        </div>

        {!activeCategory ? (
          /* CATEGORIES GRID — exactly like the original site */
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {allCategories.map((cat) => {
              const cover = getCover(cat);
              const isBroken = !cover || brokenCovers.has(cat.id);
              return (
              <button
                key={cat.id}
                onClick={() => { setActiveCategory(cat); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="gallery-item aspect-square block group relative overflow-hidden bg-zinc-900"
                data-testid={`gallery-cat-${cat.id}`}
              >
                {isBroken ? (
                  <div className="absolute inset-0 bg-gradient-to-br from-zinc-900 via-zinc-950 to-black flex items-center justify-center">
                    <Camera size={42} className="text-gold/30" strokeWidth={1.2} />
                  </div>
                ) : (
                  <img
                    src={cover}
                    alt={cat.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={() => {
                      setBrokenCovers((prev) => {
                        const next = new Set(prev);
                        next.add(cat.id);
                        return next;
                      });
                    }}
                  />
                )}
                <div className="absolute inset-0 bg-black/50 group-hover:bg-black/30 transition-colors duration-500" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <h3
                      className="text-2xl md:text-3xl text-white italic transform group-hover:scale-110 transition-transform duration-500"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, textShadow: "0 2px 10px rgba(0,0,0,0.8)" }}
                    >
                      {cat.name}
                    </h3>
                    <div className="w-12 h-px bg-gold mx-auto mt-3 transition-all duration-500 group-hover:w-20" />
                  </div>
                </div>
              </button>
              );
            })}
          </div>
        ) : (
          /* SUB-GALLERY for selected category */
          <div>
            <button
              onClick={() => setActiveCategory(null)}
              className="mb-8 flex items-center gap-2 text-gold hover:text-gold-light text-sm uppercase tracking-wider transition-colors"
            >
              <ArrowLeft size={16} />
              Retour aux galeries
            </button>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {images.map((img, idx) => (
                <div
                  key={`${img}-${idx}`}
                  onClick={() => openLightbox(idx)}
                  className="gallery-item aspect-[3/4] cursor-pointer"
                >
                  <img
                    src={img}
                    alt={`${activeCategory.name} ${idx + 1}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
              ))}
            </div>

            {images.length === 0 && (
              <p className="text-center text-gray-500 py-20">Photos à venir dans cette catégorie.</p>
            )}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="lightbox-overlay" onClick={() => setLightbox(null)}>
          <button
            onClick={(e) => { e.stopPropagation(); setLightbox(null); }}
            className="absolute top-6 right-6 text-white hover:text-gold z-10"
          >
            <X size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); showNav(-1); }}
            className="absolute left-6 text-white hover:text-gold z-10"
          >
            <ChevronLeft size={40} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); showNav(1); }}
            className="absolute right-6 text-white hover:text-gold z-10"
          >
            <ChevronRight size={40} />
          </button>
          <img
            src={lightbox}
            alt="Photo"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-gold text-sm tracking-wider uppercase">
            {activeCategory?.name} — {lightboxIdx + 1} / {images.length}
          </p>
        </div>
      )}
    </div>
  );
}
